import type { CSSProperties, ReactNode } from 'react';
import { useLabelPlacement, type LabelPlacement } from './labelPlacement';
import { fieldWidthStyle, sizesControl } from './fieldUtils';
import './form.css';

/**
 * The frame every shared form field uses: label, control, then helper and
 * error text below. Where the label goes is `labelPlacement`:
 *
 * - 'border': on the field's outline, in a notch, as MUI outlined fields
 *   draw it (and DevExtreme's did). The control renders that label itself (see
 *   `borderLabel` in labelPlacement.tsx), so this frame renders none and only leaves room above.
 * - 'above': a <label> above the field, outside the border.
 * - 'left': a <label> beside the field on the same row, --field-label-width
 *   wide (default 140px), vertically centred on the field. Below 600px it
 *   wraps to above.
 *
 * Every placement uses a real <label htmlFor={inputId}>, so the control's
 * accessible name is exactly the visible text, and none repeats the label as
 * a placeholder. Pass `label` undefined when the visible label lives
 * elsewhere (the control then takes an aria-label instead).
 *
 * When `labelPlacement` is not passed, the app-wide setting applies.
 */
export interface FieldProps {
  inputId: string;
  label?: string;
  required?: boolean;
  helperText?: ReactNode;
  helperId?: string;
  errorText?: string;
  errorId?: string;
  /** Field is invalid: the label turns the error colour in every placement. */
  error?: boolean;
  disabled?: boolean;
  /** The control's width (a number is px). Beside a label ('left') a px width
   *  is the box alone and the label column is added outside it; a relative
   *  width ('100%') is the whole field's. */
  width?: number | string;
  /** Pins the field height in px (sets --app-field-height for the control inside). */
  height?: number;
  /** Where the label goes. Default: the app-wide setting. */
  labelPlacement?: LabelPlacement;
  /** Small controls that belong to the label, e.g. an info icon or a "copy
   *  from Breakfast" button: drawn right after the label text in every
   *  placement (on the border they sit on the outline, just past the label).
   *  Never inside the <label>, so the field's name stays the label alone. */
  labelAdornment?: ReactNode;
  className?: string;
  children: ReactNode;
}

export default function Field({
  inputId, label, required, helperText, helperId, errorText, errorId, error, disabled, width, height, labelPlacement, labelAdornment, className, children,
}: FieldProps) {
  const placement = useLabelPlacement(labelPlacement);
  const style = { ...fieldWidthStyle(width, placement, !!label), ...(height ? { '--app-field-height': `${height}px` } : {}) } as CSSProperties;
  const classes = [
    'app-field',
    `app-field--${placement}`,
    label && 'app-field--labelled',
    sizesControl(width, placement, !!label) && 'app-field--sized',
    (error || errorText) && 'app-field--invalid',
    disabled && 'app-field--disabled',
    label && labelAdornment && 'app-field--adorned',
    className,
  ].filter(Boolean).join(' ');
  return (
    <div className={classes} style={style} data-label-placement={placement}>
      {label && placement !== 'border' && (labelAdornment ? (
        <div className="app-field-label app-field-label-row">
          <label htmlFor={inputId}>
            {label}
            {required && <span className="app-field-required" aria-hidden="true">*</span>}
          </label>
          {labelAdornment}
        </div>
      ) : (
        <label className="app-field-label" htmlFor={inputId}>
          {label}
          {required && <span className="app-field-required" aria-hidden="true">*</span>}
        </label>
      ))}
      {children}
      {label && placement === 'border' && labelAdornment && (
        // On the outline, just past the label: an invisible copy of the
        // label takes its width, so the controls start where it ends.
        <div className="app-field-border-adorn">
          <span className="app-field-border-adorn-ghost" aria-hidden="true">
            {label}
            {required && <span className="app-field-required">*</span>}
          </span>
          <span className="app-field-border-adorn-items">{labelAdornment}</span>
        </div>
      )}
      {errorText && <div id={errorId} className="app-field-error">{errorText}</div>}
      {helperText && <div id={helperId} className="app-field-helper">{helperText}</div>}
    </div>
  );
}
